'use strict'

import Polygon from './Polygon.js'
import CXImageAction from './ImagePaintProcessing/CXImageAction.js'
import CXGrayImagePixelMutableArray from './ImagePaintProcessing/CXGrayImagePixelMutableArray.js'
import Config from './Config.js'

/**
 * PolygonManager
 * --------------------------------------------------------------------
 * Owns polygon drawing state and converts polygon pixels into mask actions.
 * Keeps CanvasState focused on orchestration instead of polygon bookkeeping.
 */
class PolygonManager {
    /**
     * @param {Object} params
     * @param {CanvasState} params.canvasState
     */
    constructor({ canvasState }) {
        this.canvasState = canvasState
        this.polygons = []
    }

    /**
     * Reset polygon state for a new project.
     * @returns {void}
     */
    reset() {
        this.polygons = []
    }

    /**
     * Return the current polygon being drawn.
     * @returns {Polygon|undefined}
     */
    getCurrentPolygon() {
        return this.polygons[this.polygons.length - 1]
    }

    /**
     * Start a new polygon and register it in the stack.
     * @param {Object} opts
     * @param {number} opts.x
     * @param {number} opts.y
     * @param {Object} opts.color
     * @param {number|string} opts.numcolor
     * @returns {Polygon}
     */
    startPolygon({ x, y, color, numcolor }) {
        const cs = this.canvasState
        const polygon = new Polygon({
            x,
            y,
            ctx: cs.ctx,
            viewimage_draw: cs.viewimage_draw.element,
            color,
            numcolor: parseInt(numcolor, 10),
            mindist: cs._ratioCanvas,
        })
        this.polygons.push(polygon)
        return polygon
    }

    /**
     * Register an existing polygon instance.
     * @param {Polygon} polygon
     * @returns {void}
     */
    registerPolygon(polygon) {
        if (!polygon) return
        this.polygons.push(polygon)
    }

    /**
     * Discard the last polygon in the stack.
     * @returns {Polygon|undefined}
     */
    discardLastPolygon() {
        return this.polygons.pop()
    }

    /**
     * Finalize the current polygon and apply its pixels to the mask.
     * @param {boolean} fill
     * @returns {void}
     */
    finishPolygon(fill) {
        const cs = this.canvasState
        const current = this.getCurrentPolygon()
        if (!current) return

        cs.isPainting = false
        cs.viewimage_draw.context.clearRect(0, 0, cs._realImageWidth, cs._realImageHeight)
        cs.viewimage.loadData()

        this.commitPolygonPixels(fill, current.finish())
        current.clear()
    }

    /**
     * Save current polygon in history (poly plus) or delete (poly minus).
     * @param {boolean} fill
     * @param {Map<number, {x:number,y:number}>} pixels
     * @returns {void}
     */
    commitPolygonPixels(fill, pixels) {
        const cs = this.canvasState
        const defaultMask = Config.DEFAULT_MASKING_COLOR

        if (fill && typeof pixels !== 'undefined' && pixels.size > 0) {
            // add action
            const action = new CXImageAction(CXImageAction.ActionType.ActionTypePaint)
            action.setColorSample(cs.colorSample)

            // add pixels to action, refresh ui, update mask
            pixels.forEach(function (value, key) {
                if (this._maskImage.data[key] === defaultMask) {
                    action.lastLevelpixels().addPixelwithValue(key, this.colorSample.workingColorPos)
                    this._maskImage.data[key] = this.colorSample.workingColorPos
                    this.refreshResultAtPosition([key, this.colorSample.workingColorPos])
                }
            }, cs)

            cs.viewimage.context.putImageData(cs.viewimage.data, 0, 0)
            cs.addAction(action)
        } else {
            const action = new CXImageAction(CXImageAction.ActionType.ActionTypeErase)

            // add pixels to action, refresh ui, update mask
            pixels.forEach(function (value, key) {
                action.lastLevelpixels().addPixelwithValue(key, this._maskImage.data[key])
                this._maskImage.data[key] = defaultMask
                this.refreshResultAtPosition([key, defaultMask])
            }, cs)

            cs.addAction(action)
            cs.viewimage.context.putImageData(cs.viewimage.data, 0, 0)
        }
    }

    /**
     * Convert pixel coordinates into a mutable pixel list.
     * @param {Map<number, {x:number,y:number}>} points
     * @param {number} position
     * @returns {CXGrayImagePixelMutableArray}
     */
    buildPixelArrayFromPoints(points, position) {
        console.info('buildPixelArrayFromPoints: ' + position)
        const cs = this.canvasState

        points.forEach(function (value, key) {
            if (value.x >= cs._realImageWidth || value.x < 0) {
                points.delete(key)
            }
        })

        const pixels = new CXGrayImagePixelMutableArray()
        points.forEach(function (value, key) {
            pixels.addPixelwithValue(key, position)
        })

        return pixels
    }
}

export default PolygonManager
